// DailyRevenuePanel — Phase 5a daily endpoint.

import { useState } from 'react';
import { Card } from '../../primitives';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  MiniBars,
  SectionHead,
} from '../bits';
import { useDaily, type AnalyticsScope } from '../queries';
import { fmtWonShort } from '../../../lib/format';

interface Props {
  scope: AnalyticsScope;
}

export function DailyRevenuePanel({ scope }: Props) {
  const q = useDaily(scope);
  const [hover, setHover] = useState<number | null>(null);

  return (
    <Card style={{ padding: 20 }}>
      <SectionHead title="일별 매출" sub="기간 내 일별" />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.data && q.data.length === 0 && <EmptyState />}
      {q.data && q.data.length > 0 && (
        <DailyBody data={q.data} hover={hover} setHover={setHover} />
      )}
    </Card>
  );
}

function DailyBody({
  data,
  hover,
  setHover,
}: {
  data: { date: string; revenue: number; orders: number }[];
  hover: number | null;
  setHover: (i: number | null) => void;
}) {
  const revs = data.map((d) => d.revenue);
  const total = revs.reduce((a, b) => a + b, 0);
  const avg = revs.length ? Math.round(total / revs.length) : 0;
  const peakIdx = revs.indexOf(Math.max(...revs));
  const idx = hover != null ? hover : peakIdx;
  const point = data[idx];
  const dateLabel = point?.date ?? '';
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--fg-3)' }}>{dateLabel}</span>
        <span
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--fg-1)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fmtWonShort(point?.revenue ?? 0)}
        </span>
        {avg > 0 && point && (
          <span
            style={{
              fontSize: 12,
              color:
                point.revenue >= avg
                  ? 'var(--success)'
                  : 'var(--fg-3)',
            }}
          >
            {point.revenue >= avg ? '+' : ''}
            {(((point.revenue - avg) / avg) * 100).toFixed(1)}% vs 평균
          </span>
        )}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 12,
            color: 'var(--fg-3)',
          }}
        >
          합계{' '}
          <span
            style={{
              color: 'var(--fg-1)',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtWonShort(total)}
          </span>
        </span>
      </div>
      <MiniBars
        data={revs}
        height={64}
        activeIndex={hover}
        onHover={setHover}
      />
    </>
  );
}
